import type { WorkspaceTab } from './workspaceViewModel';

export type WorkspaceExpandLevel = 'collapsed' | 'dashboard' | 'full';

export interface WorkspacePanelState {
  expandLevel: WorkspaceExpandLevel;
  activeTab: WorkspaceTab;
  lastAutoOpenedMessageId: string | null;
  dismissedAutoOpenMessageIds: string[];
}

export type WorkspacePanelStateBySession = Record<string, WorkspacePanelState>;

export const DRAFT_WORKSPACE_PANEL_SESSION_KEY = '__draft__';

export function getWorkspacePanelSessionKey(sessionId: string | null): string {
  return sessionId || DRAFT_WORKSPACE_PANEL_SESSION_KEY;
}

export function createDefaultWorkspacePanelState(): WorkspacePanelState {
  return {
    expandLevel: 'collapsed',
    activeTab: 'overview',
    lastAutoOpenedMessageId: null,
    dismissedAutoOpenMessageIds: [],
  };
}

export function getWorkspacePanelState(
  stateBySession: WorkspacePanelStateBySession,
  sessionId: string | null,
): WorkspacePanelState {
  return stateBySession[getWorkspacePanelSessionKey(sessionId)] || createDefaultWorkspacePanelState();
}

export function setWorkspacePanelExpandLevel(
  stateBySession: WorkspacePanelStateBySession,
  sessionId: string | null,
  expandLevel: WorkspaceExpandLevel,
): WorkspacePanelStateBySession {
  const key = getWorkspacePanelSessionKey(sessionId);
  const current = getWorkspacePanelState(stateBySession, sessionId);
  return {
    ...stateBySession,
    [key]: {
      ...current,
      expandLevel,
    },
  };
}

export function resetWorkspacePanelState(
  stateBySession: WorkspacePanelStateBySession,
  sessionId: string | null,
): WorkspacePanelStateBySession {
  return {
    ...stateBySession,
    [getWorkspacePanelSessionKey(sessionId)]: createDefaultWorkspacePanelState(),
  };
}

export function setWorkspacePanelActiveTab(
  stateBySession: WorkspacePanelStateBySession,
  sessionId: string | null,
  activeTab: WorkspaceTab,
): WorkspacePanelStateBySession {
  const key = getWorkspacePanelSessionKey(sessionId);
  const current = getWorkspacePanelState(stateBySession, sessionId);
  return {
    ...stateBySession,
    [key]: {
      ...current,
      activeTab,
    },
  };
}

export function dismissWorkspacePanelAutoOpen(
  stateBySession: WorkspacePanelStateBySession,
  sessionId: string | null,
  messageId: string,
): WorkspacePanelStateBySession {
  const key = getWorkspacePanelSessionKey(sessionId);
  const current = getWorkspacePanelState(stateBySession, sessionId);
  if (current.dismissedAutoOpenMessageIds.includes(messageId)) return stateBySession;
  return {
    ...stateBySession,
    [key]: {
      ...current,
      dismissedAutoOpenMessageIds: [...current.dismissedAutoOpenMessageIds, messageId],
    },
  };
}

export function applyWorkspacePanelAutoOpen(
  stateBySession: WorkspacePanelStateBySession,
  sessionId: string | null,
  messageId: string,
): WorkspacePanelStateBySession {
  const key = getWorkspacePanelSessionKey(sessionId);
  const current = getWorkspacePanelState(stateBySession, sessionId);
  if (
    current.lastAutoOpenedMessageId === messageId
    || current.dismissedAutoOpenMessageIds.includes(messageId)
  ) {
    return stateBySession;
  }

  return {
    ...stateBySession,
    [key]: {
      ...current,
      lastAutoOpenedMessageId: messageId,
      expandLevel: current.expandLevel === 'collapsed' ? 'dashboard' : current.expandLevel,
    },
  };
}
